/**
 * <regime-step-switcher>
 *
 * Wraps the "2 STEP / 3 STEP / 4 STEP" pills. On click, fetches the
 * sibling product's page via Shopify's Section Rendering API
 * (?section_id=...) and swaps the media gallery + product info
 * containers in place — no full page reload.
 *
 * Requires the pills to be <a data-regime-step href="{{ step_product.url }}">
 * and the section to keep its existing ids:
 *   #MediaGallery-{{ section.id }}
 *   #ProductInfo-{{ section.id }}
 */
class RegimeStepSwitcher extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.onPillClick = this.onPillClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onPillClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onPillClick);
  }

  onPillClick(event) {
    const pill = event.target.closest('[data-regime-step]');
    if (!pill || !this.contains(pill)) return;
    if (pill.classList.contains('is-active') || this.classList.contains('is-loading')) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.loadStep(pill);
  }

  async loadStep(pill) {
    const url = pill.getAttribute('href');
    this.setLoading(true);

    try {
      const response = await fetch(`${url}?section_id=${this.sectionId}`);
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const swapped = [
        this.swapElement(doc, `#MediaGallery-${this.sectionId}`),
        this.swapElement(doc, `#ProductInfo-${this.sectionId}`),
      ];

      if (!swapped.every(Boolean)) {
        throw new Error('Could not find expected section elements to swap');
      }

      window.history.pushState({ regimeStep: url }, '', url);
      document.title = doc.title;

      this.setActivePill(pill);

      document.dispatchEvent(
        new CustomEvent('regime:step-loaded', { bubbles: true, detail: { url, sectionId: this.sectionId } })
      );
    } catch (error) {
      console.error('[regime-step-switcher] falling back to full navigation:', error);
      window.location.href = url;
    } finally {
      this.setLoading(false);
    }
  }

  swapElement(newDoc, selector) {
    const newEl = newDoc.querySelector(selector);
    const currentEl = document.querySelector(selector);
    if (!newEl || !currentEl) return false;
    currentEl.replaceWith(newEl);
    return true;
  }

  setActivePill(activePill) {
    // The pills live inside #ProductInfo, which was just replaced wholesale,
    // so re-query the (new) regime-step-switcher instance(s) in the DOM.
    document.querySelectorAll('regime-step-switcher [data-regime-step]').forEach((el) => {
      const isActive = el.getAttribute('href') === activePill.getAttribute('href');
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-selected', String(isActive));
      el.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive) {
        el.setAttribute('aria-current', 'true');
      } else {
        el.removeAttribute('aria-current');
      }
    });
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    const mediaGallery = document.querySelector(`#MediaGallery-${this.sectionId}`);
    const productInfo = document.querySelector(`#ProductInfo-${this.sectionId}`);
    [mediaGallery, productInfo].forEach((el) => {
      if (el) el.classList.toggle('regime-step-loading', isLoading);
    });
  }
}

if (!customElements.get('regime-step-switcher')) {
  customElements.define('regime-step-switcher', RegimeStepSwitcher);
}

// Support browser back/forward through swapped states.
window.addEventListener('popstate', (event) => {
  if (event.state && event.state.regimeStep) {
    window.location.reload();
  }
});